import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import teacherImage from '../assets/images/teachers/Whisk_ffb4401f1808c69b6fb43f34cbc75ae4dr.png';
import FloatingEnglishIcons from '../components/common/FloatingBiologyIcons';

// Grade Card Images
import gradeImage1 from '../assets/images/books/كتب أولي.png';
import gradeImage2 from '../assets/images/books/كتب تانية.png';
import gradeImage3 from '../assets/images/books/كتب تالتة.png';

// Import UnitCodePopup
import UnitCodePopup from '../components/common/UnitCodePopup';
import { useContact } from '../hooks/useContact';

// Lazy load heavy components
const CourseList = lazy(() => import('../components/course/CourseList'));
const BooksList = lazy(() => import('../components/common').then(module => ({ default: module.BooksList })));
const CodeEntryForm = lazy(() => import('../components/common').then(module => ({ default: module.CodeEntryForm })));
const GradeCards = lazy(() => import('../components/common').then(module => ({ default: module.GradeCards })));
const TopStudents = lazy(() => import('../components/common/TopStudents'));

// Optimized icon imports - only import what we need
import { 
  ArrowRight, 
  BookOpen, 
  Users, 
  Play, 
  Target, 
  MessageCircle, 
  Zap,
  Trophy,
  Brain,
  Sparkles,
  Check,
  Award,
  Star,
  ClipboardList,
  Library,
  Wrench,
  Gift
} from 'lucide-react';

type FeatureCardItem = {
  Icon: typeof ClipboardList;
  title: string;
  description: string;
  iconWrapperClassName: string;
  iconClassName: string;
};

const FEATURE_CARDS: FeatureCardItem[] = [
  {
    Icon: ClipboardList,
    title: 'مراجعة مستمرة',
    description: 'امتحانات أسبوعية وشهرية حتى لا تنسي ما تعلمته',
    iconWrapperClassName: 'bg-blue-50',
    iconClassName: 'text-blue-600',
  },
  {
    Icon: Library,
    title: 'كل الملازم',
    description: 'توفير كافة الملازم لجميع المراحل الدراسية مع الحلول النموذجية لها',
    iconWrapperClassName: 'bg-emerald-50',
    iconClassName: 'text-emerald-600',
  },
  {
    Icon: Wrench,
    title: 'دعم 24 ساعة',
    description: 'فريق الدعم موجود معاك في أي وقت لو عندك استفسار أو مشكلة',
    iconWrapperClassName: 'bg-orange-100',
    iconClassName: 'text-gray-900',
  },
  {
    Icon: Gift,
    title: 'تشجيع وهدايا',
    description: 'هدايا مميزة للطلاب المتفوقين',
    iconWrapperClassName: 'bg-rose-50',
    iconClassName: 'text-rose-600',
  },
];

type HeroTeacherFigureProps = { teacherImage: string };

const HeroTeacherFigure: React.FC<HeroTeacherFigureProps> = ({ teacherImage }) => (
  <figure className="relative mx-auto max-w-md lg:max-w-none">
    <div
      className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-orange-400/20 via-amber-300/12 to-transparent blur-2xl"
      aria-hidden
    />
    <div className="relative overflow-hidden rounded-3xl bg-white p-2 shadow-[0_28px_64px_-16px_rgba(234,88,12,0.26)] ring-1 ring-slate-200/85">
      <img
        src={teacherImage}
        alt="صورة المعلم مستر وليد المصري"
        className="h-auto w-full rounded-2xl object-contain"
      />
    </div>
    <figcaption className="pointer-events-none absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-white px-3 py-1.5 text-xs font-bold text-emerald-700 shadow-md ring-1 ring-emerald-200/90 sm:text-sm lg:left-auto lg:right-5 lg:translate-x-0">
      <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />
      معك لحد الامتحان
    </figcaption>
  </figure>
);

// Optimized floating icons - reduced count and simplified animations
const createOptimizedIcons = (section: string = 'default') => {
  const icons = [
    // Reduced from 21 to 8 icons for better performance
    { src: 'anubis', top: '5%', left: '5%', size: '35px', delay: 0 },
    { src: 'hieroglyph', top: '8%', left: '25%', size: '40px', delay: 0.5 },
    { src: 'pharaoh', top: '12%', left: '45%', size: '45px', delay: 1 },
    { src: 'globe', top: '6%', left: '65%', size: '38px', delay: 1.5 },
    { src: 'world', top: '15%', left: '85%', size: '60px', delay: 2 },
    { src: 'anubis', top: '45%', left: '12%', size: '34px', delay: 2.5 },
    { src: 'hieroglyph', top: '65%', left: '52%', size: '41px', delay: 3 },
    { src: 'pharaoh', top: '85%', left: '38%', size: '44px', delay: 3.5 },
  ];

  return icons.map((icon, index) => (
    <div
      key={`${section}-icon-${index}`}
      className="absolute pointer-events-none z-10 opacity-15"
      style={{
        top: icon.top,
        left: icon.left,
        width: icon.size,
        height: icon.size,
        animationDelay: `${icon.delay}s`,
        animationDuration: '4s',
        animationIterationCount: 'infinite',
        animationTimingFunction: 'ease-in-out',
        willChange: 'transform'
      }}
    >
      <div className="w-full h-full bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full animate-pulse"></div>
    </div>
  ));
};

// Memoized section component
type PremiumSectionProps = {
  children: React.ReactNode;
  title: string;
  badgeText: string;
  icon: React.ComponentType<any>;
  id?: string;
} & React.HTMLAttributes<HTMLElement>;

const PremiumSection = React.memo(({ 
  children, 
  title, 
  badgeText, 
  icon: Icon,
  id,
  ...rest
}: PremiumSectionProps) => (
     <section id={id} className="py-6 sm:py-12 lg:py-16 bg-gradient-to-br from-white via-blue-50/30 to-purple-50/30 relative overflow-hidden" {...rest}>
    {/* Simplified decorative borders */}
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
    <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"></div>
    
    {/* Simplified background elements */}
    <div className="absolute inset-0">
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full blur-2xl"></div>
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-br from-purple-400/10 to-blue-400/10 rounded-full blur-2xl"></div>
    </div>
    
    {/* Floating icons */}
    <div className="absolute inset-0 pointer-events-none z-10">
      {createOptimizedIcons(title.toLowerCase())}
    </div>
    
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      {/* Merged header and content container */}
      <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Header section at the top */}
                 

        {/* Content section below header */}
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="relative z-10">
            {children}
          </div>
        </div>
      </div>
    </div>
  </section>
));

PremiumSection.displayName = 'PremiumSection';

const Home: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isUnitCodePopupOpen, setIsUnitCodePopupOpen] = useState(false);
  const { openWhatsApp, whatsappNumber } = useContact();

  // Memoized scroll function
  const scrollToCourses = useCallback(() => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Optimized testimonials carousel with useCallback
  const handleTestimonialChange = useCallback(() => {
    setCurrentTestimonial((prev) => (prev + 1) % 3);
  }, []);

  // WhatsApp contact handler for unit codes
  const handleWhatsAppUnitCode = useCallback(() => {
    if (whatsappNumber) {
      const message = 'مرحباً، أريد الحصول على كود المحاضرة';
      openWhatsApp(message);
    }
  }, [whatsappNumber, openWhatsApp]);

  // WhatsApp contact handler for general support
  const handleWhatsAppSupport = useCallback(() => {
    if (whatsappNumber) {
      const message = 'مرحباً، أريد المساعدة';
      openWhatsApp(message);
    }
  }, [whatsappNumber, openWhatsApp]);

  // Entrance animation
  useEffect(() => {
    setIsVisible(true);
    AOS.init({ duration: 800, easing: 'ease', once: true, offset: 40 });
    AOS.refresh();
  }, []);
  
  // Testimonials carousel with useCallback
  useEffect(() => {
    const interval = setInterval(handleTestimonialChange, 4000);
    return () => clearInterval(interval);
  }, [handleTestimonialChange]);

  return (
    <>
      <Helmet>
        {/* Lemonada font for hero heading */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Lemonada:wght@300..700&family=Playpen+Sans+Arabic:wght@100..800&display=swap" rel="stylesheet" />
        {/* Primary Meta Tags */}
        <title>منصة مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية | منصة تعليمية متكاملة</title>
        <meta name="title" content="منصة مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية | منصة تعليمية متكاملة" />
        <meta name="description" content="مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية. منصة تعليمية متكاملة تقدم دروساً وشروحات في اللغة الإنجليزية لأولى وثانية وثالثة ثانوي. درجة الإنجليزي مضمونة مع شرح مناسب لكل المستويات." />
        <meta name="keywords" content="مستر وليد المصري, وليد المصري, مدرس انجليزي ثانوي, تعليم اونلاين, دروس انجليزي, شرح انجليزي, منصة تعليمية, انجليزي ثانوية عامة, أولى ثانوي, تانية ثانوي, تالتة ثانوي, English teacher Egypt secondary" />
        <meta name="author" content="مستر وليد المصري" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://mohamed-said.com/" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mohamed-said.com/" />
        <meta property="og:title" content="منصة مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية | منصة تعليمية متكاملة" />
        <meta property="og:description" content="مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية. منصة تعليمية متكاملة تقدم دروساً وشروحات في اللغة الإنجليزية لطلاب الثانوية. درجة الإنجليزي مضمونة!" />
        <meta property="og:image" content="https://mohamed-said.com/favicon.png" />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        <meta property="og:image:alt" content="منصة مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية" />
        <meta property="og:site_name" content="منصة مستر وليد المصري التعليمية" />
        <meta property="og:locale" content="ar_AR" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://mohamed-said.com/" />
        <meta property="twitter:title" content="منصة مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية | منصة تعليمية متكاملة" />
        <meta property="twitter:description" content="مستر وليد المصري - مدرس اللغة الإنجليزية للمرحلة الثانوية. منصة تعليمية متكاملة تقدم دروساً وشروحات في اللغة الإنجليزية. درجة الإنجليزي مضمونة!" />
        <meta property="twitter:image" content="https://mohamed-said.com/favicon.png" />
        <meta property="twitter:creator" content="@mohamed_said" />

        {/* Additional SEO Meta Tags */}
        <meta name="language" content="Arabic" />
        <meta name="geo.region" content="EG" />
        <meta name="geo.placename" content="Egypt" />
        <meta name="geo.position" content="30.0444;31.2357" />
        <meta name="ICBM" content="30.0444, 31.2357" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        <meta name="revisit-after" content="7 days" />
        <meta name="generator" content="React" />
        <meta name="subject" content="تعليم اللغة الإنجليزية" />
        <meta name="category" content="تعليم" />
        <meta name="classification" content="منصة تعليمية" />
        <meta name="coverage" content="Worldwide" />
        <meta name="target" content="طلاب المدارس والجامعات" />
        <meta name="HandheldFriendly" content="true" />
        <meta name="MobileOptimized" content="width" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content="منصة مستر وليد المصري" />
        <meta name="msapplication-TileColor" content="#f97316" />
        <meta name="msapplication-TileImage" content="/favicon.png" />

        {/* Structured Data for Teacher/Person */}
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "مستر وليد المصري",
          "alternateName": ["وليد المصري", "Walid El-Masry", "مستر وليد"],
          "jobTitle": "مدرس اللغة الإنجليزية للمرحلة الثانوية",
          "description": "مدرس لغة إنجليزية للمرحلة الثانوية — منصة تعليمية لأولى وثانية وثالثة ثانوي",
          "url": "https://mohamed-said.com/",
          "image": "https://mohamed-said.com/favicon.png",
          "sameAs": [
            "https://www.facebook.com/mohamed.said",
            "https://www.youtube.com/@mohamedsaid",
            "https://www.instagram.com/mohamed_said"
          ],
          "worksFor": {
            "@type": "Organization",
            "name": "منصة مستر وليد المصري التعليمية",
            "url": "https://mohamed-said.com/"
          },
          "knowsAbout": [
            "اللغة الإنجليزية",
            "English Grammar", 
            "English Conversation",
            "IELTS",
            "تعليم الإنجليزية",
            "المناهج الدراسية",
            "التحضير للامتحانات",
            "الدروس الخصوصية"
          ],
          "hasOccupation": {
            "@type": "Occupation",
            "name": "مدرس خصوصي",
            "occupationLocation": {
              "@type": "Place",
              "name": "مصر"
            }
          },
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "EG",
            "addressRegion": "القاهرة"
          },
          "telephone": "+20-100-000-0000",
          "email": "mohamed@mohamed-said.com"
        })}
        </script>

        {/* Structured Data for WebSite */}
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "منصة مستر وليد المصري التعليمية",
          "url": "https://mohamed-said.com/",
          "description": "منصة تعليمية متكاملة تقدم دروساً وشروحات في اللغة الإنجليزية",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://mohamed-said.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
        </script>

        {/* Structured Data for Educational Organization */}
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "منصة مستر وليد المصري التعليمية",
          "url": "https://mohamed-said.com/",
          "description": "منصة تعليمية متخصصة في تدريس اللغة الإنجليزية لطلاب المرحلة الثانوية",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "EG",
            "addressRegion": "القاهرة"
          },
          "telephone": "+20-100-000-0000",
          "email": "info@mohamed-said.com",
          "founder": {
            "@type": "Person",
            "name": "مستر وليد المصري"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "دورات اللغة الإنجليزية",
            "itemListElement": [
              {
                "@type": "Course",
                "name": "دورة الصف الأول الثانوي",
                "description": "دورة شاملة في اللغة الإنجليزية للصف الأول الثانوي"
              },
              {
                "@type": "Course", 
                "name": "دورة الصف الثاني الثانوي",
                "description": "دورة شاملة في اللغة الإنجليزية للصف الثاني الثانوي"
              },
              {
                "@type": "Course",
                "name": "دورة الصف الثالث الثانوي", 
                "description": "دورة شاملة في اللغة الإنجليزية للصف الثالث الثانوي"
              }
            ]
          }
        })}
        </script>
      </Helmet>
      
      <div className="min-h-[100svh] bg-white" dir="rtl">
      {/* Global Floating English Icons */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <FloatingEnglishIcons 
          count={20}
          opacity={0.05}
          minSize={20}
          maxSize={35}
          sizeScale={0.5}
        />
      </div>
      
      {/* Hero Section */}
      <section
        className="hero-section relative overflow-hidden bg-gradient-to-b from-slate-50 via-white to-orange-50/40 lg:min-h-[100svh]"
        aria-labelledby="hero-heading"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <div className="absolute -left-[25%] top-[10%] h-[min(90vw,28rem)] w-[min(90vw,28rem)] rounded-full bg-orange-200/25 blur-3xl" />
          <div className="absolute -right-[20%] bottom-[5%] h-[min(80vw,24rem)] w-[min(80vw,24rem)] rounded-full bg-amber-200/20 blur-3xl" />
          <div className="absolute left-1/2 top-[22%] h-px w-[min(92%,56rem)] -translate-x-1/2 bg-gradient-to-r from-transparent via-orange-200/60 to-transparent" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col justify-start px-4 pb-14 pt-2 sm:px-6 sm:pb-20 sm:pt-4 lg:grid lg:min-h-[min(100svh,56rem)] lg:grid-cols-2 lg:items-center lg:justify-center lg:gap-12 lg:pb-24 lg:pt-12">
          {/* نص + أزرار */}
          <div
            className="order-1 flex flex-col text-center md:text-right"
            data-aos="fade-up"
            data-aos-delay="100"
            data-aos-anchor-placement="top-bottom"
          >
            <span className="mx-auto mb-5 inline-flex items-center gap-2 rounded-full border border-orange-200/90 bg-white/90 px-4 py-2 text-sm font-semibold text-orange-700 shadow-sm backdrop-blur-sm md:mx-0">
              <Sparkles className="h-4 w-4 shrink-0 text-amber-500" strokeWidth={2} />
              ثانوية عامة · لغة إنجليزية
            </span>

            <h1
              id="hero-heading"
              className="overflow-visible font-extrabold leading-[1.12] text-slate-900"
              style={{ fontFamily: 'Lemonada, system-ui, Arial' }}
            >
              <span className="block text-3xl sm:text-4xl md:text-[2.65rem]">منصة</span>
              <span className="mt-2 block text-4xl font-extrabold leading-[1.25] text-orange-600 sm:text-5xl md:text-[3.15rem] [text-shadow:0_1px_0_rgba(255,255,255,0.4)]">
                مستر / وليد المصري
              </span>
            </h1>

            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg md:mx-0">
              منصة تعليمية لتدريس اللغة الانجليزية لطلاب المرحلة الثانوية
            </p>

            {/* صورة المعلم — موبايل فقط */}
            <div className="mx-auto mt-8 w-full max-w-md lg:hidden" data-aos="fade-up" data-aos-delay="50">
              <HeroTeacherFigure teacherImage={teacherImage} />
            </div>

            <div
              className="mx-auto mt-8 w-full max-w-xl rounded-2xl border border-slate-200/90 bg-white/85 p-5 shadow-[0_20px_50px_-24px_rgba(15,23,42,0.12)] backdrop-blur-sm sm:p-7 md:mx-0"
              data-aos="fade-up"
              data-aos-delay="150"
            >
              <div className="space-y-3 text-right text-base leading-relaxed text-slate-700 sm:text-lg">
                <p className="font-semibold text-slate-800">
                  سواء كنت أولى أو ثانية أو ثالثة ثانوية و حاسس إنك تايه بين الكلمات و القواعد
                </p>
                <p>والقراءة و الكتابة في اللغة الإنجليزية</p>
                <p className="font-bold text-slate-900">هنا على منصة مستر وليد المصري</p>
                <p>هتلاقي شرح ممتع و جذاب يناسب كل المستويات</p>
              </div>
              <div className="mt-6 border-t border-orange-100 pt-6 text-center">
                <p className="text-lg font-bold text-slate-800 sm:text-xl">شعارنا دائما</p>
                <p className="mt-2 text-2xl font-extrabold text-orange-600 sm:text-3xl">
                  العلامة الكاملة مضمونة 100 %
                </p>
              </div>
            </div>

            <div
              className="mx-auto mt-9 flex w-full max-w-md flex-col gap-3 sm:max-w-lg sm:flex-row sm:justify-center md:mx-0 md:max-w-none md:justify-start"
              data-aos="zoom-in"
              data-aos-delay="200"
            >
              <Link
                to="/register"
                className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-2xl bg-gradient-to-l from-orange-500 to-amber-500 px-6 py-3.5 text-center text-sm font-bold text-white shadow-lg shadow-orange-500/25 transition hover:brightness-105 sm:flex-initial sm:min-w-[200px]"
              >
                سجل معانا وانضم لعيلة مستر وليد المصري
              </Link>
              <button
                type="button"
                onClick={scrollToCourses}
                className="inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-2xl border-2 border-orange-400 bg-white px-6 py-3.5 text-sm font-bold text-orange-600 transition hover:border-orange-500 hover:bg-orange-50 sm:flex-initial sm:min-w-[140px]"
              >
                <BookOpen className="h-5 w-5" />
                الباقات
              </button>
            </div>
          </div>

          {/* صورة المعلم — ديسكتوب */}
          <div
            className="order-2 hidden lg:flex lg:items-center lg:justify-center"
            data-aos="fade-up"
            data-aos-delay="200"
            data-aos-anchor-placement="top-bottom"
          >
            <div className="w-full max-w-lg xl:max-w-xl">
              <HeroTeacherFigure teacherImage={teacherImage} />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Section - Why Join Us */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-orange-50/30 overflow-hidden" data-aos="fade-up" data-aos-delay="150" data-aos-anchor-placement="top-bottom">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-orange-600 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-orange-400 rounded-full blur-3xl"></div>
              </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-orange-200 rounded-full mb-6">
              <span className="text-orange-700 font-semibold text-sm">ليه تختار منصة مستر وليد المصري؟</span>
              <span className="w-2 h-2 rounded-full bg-orange-500 mr-2 animate-pulse"></span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent">
                مميزات منصتنا
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              اكتشف لماذا يختارنا آلاف الطلاب لتعلم اللغة الإنجليزية وتحقيق أحلامهم الأكاديمية
                </p>
              </div>

          {/* Feature Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-aos="fade-up" data-aos-delay="150">
            {FEATURE_CARDS.map(({ Icon, title, description, iconWrapperClassName, iconClassName }) => (
              <div
                key={title}
                className="group flex flex-col items-center text-center bg-white border border-gray-900 rounded-2xl p-6 sm:p-7 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                <div
                  className={`mb-4 flex h-16 w-16 items-center justify-center rounded-2xl ${iconWrapperClassName}`}
                  aria-hidden
                >
                  <Icon className={`h-9 w-9 ${iconClassName}`} strokeWidth={1.75} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">{title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>


        </div>
      </section>

      {/* Floating English Icons for Courses Section */}
      <div className="relative">
        <FloatingEnglishIcons 
          count={12}
          opacity={0.08}
          minSize={25}
          maxSize={40}
          sizeScale={0.6}
        />
      </div>
      
             {/* Grade Selection Section */}
       <PremiumSection id="courses-section" title="" badgeText="" icon={BookOpen} data-aos="fade-up" data-aos-delay="150">
        <Suspense fallback={<div className="text-center py-6">جاري التحميل...</div>}>
           <GradeCards />
        </Suspense>
      </PremiumSection>

       

      {/* Floating English Icons for Books Section */}
      <div className="relative">
        <FloatingEnglishIcons 
          count={10}
          opacity={0.08}
          minSize={25}
          maxSize={40}
          sizeScale={0.6}
        />
      </div>

      {/* Optimized Books Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-orange-50/30 overflow-hidden" id="books-section" data-aos="fade-up" data-aos-delay="150">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-orange-600 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-orange-400 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-orange-100 to-orange-200 rounded-full mb-6">
              <span className="text-orange-700 font-semibold text-sm">اختر مرحلتك الدراسية</span>
              <span className="w-2 h-2 rounded-full bg-orange-500 mr-2 animate-pulse"></span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent">
                الكتب الدراسية
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              اختر صفك الدراسي لتصفح الكتب المتاحة
            </p>
          </div>

          {/* Grade Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4 lg:gap-6 max-w-4xl mx-auto">
            {/* Grade Card 1 */}
            <Link to="/books/grade/أولى ثانوي" className="flex justify-center">
              <div className="relative w-full rounded-lg sm:rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <img
                  src={gradeImage1}
                  alt="أولى ثانوي"
                  className="block w-full h-auto"
                />
              </div>
            </Link>

            {/* Grade Card 2 */}
            <Link to="/books/grade/تانية ثانوي" className="flex justify-center">
              <div className="relative w-full rounded-lg sm:rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <img
                  src={gradeImage2}
                  alt="تانية ثانوي"
                  className="block w-full h-auto"
                />
              </div>
            </Link>

            {/* Grade Card 3 */}
            <Link to="/books/grade/تالتة ثانوي" className="flex justify-center">
              <div className="relative w-full rounded-lg sm:rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                <img
                  src={gradeImage3}
                  alt="تالتة ثانوي"
                  className="block w-full h-auto"
                />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Floating English Icons for Top Students Section */}
      <div className="relative">
        <FloatingEnglishIcons 
          count={10}
          opacity={0.08}
          minSize={25}
          maxSize={40}
          sizeScale={0.6}
        />
      </div>

      {/* Top Students Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-orange-50/30 to-purple-50/30 overflow-hidden" data-aos="fade-up" data-aos-delay="150">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Suspense fallback={
            <div className="text-center py-12">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-100 to-amber-100 px-6 py-3 rounded-full mb-8">
                <Trophy className="w-5 h-5 text-yellow-600 animate-bounce" />
                <span className="text-yellow-700 font-semibold">جاري تحميل قائمة المتفوقين...</span>
              </div>
            </div>
          }>
            <TopStudents />
          </Suspense>
        </div>
      </section>

      {/* Floating English Icons for Unit Codes Section */}
      <div className="relative">
        <FloatingEnglishIcons 
          count={8}
          opacity={0.08}
          minSize={25}
          maxSize={40}
          sizeScale={0.6}
        />
      </div>

      {/* Unit Code Access Section */}
      <section className="relative py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-orange-50/30 to-white overflow-hidden" data-aos="fade-up" data-aos-delay="150">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 right-10 w-32 h-32 bg-orange-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-10 w-40 h-40 bg-orange-600 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Main Question */}
          <div className="mb-8">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent">
                معاك كود المحاضرة ؟
              </span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              ادخل الكود الخاص بك للوصول للمحاضرة مباشرة
            </p>
          </div>

          {/* Click Button */}
          <button
            onClick={() => setIsUnitCodePopupOpen(true)}
            className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 overflow-hidden"
          >
            {/* Button Background Animation */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-orange-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Button Content */}
            <div className="relative z-10 flex items-center gap-3">
              <Play className="w-6 h-6 group-hover:rotate-12 transition-transform duration-300" />
              <span className="text-xl">دوس لو معاك كود المحاضرة</span>
              <div className="w-2 h-2 bg-white rounded-full group-hover:animate-pulse"></div>
            </div>
            
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-500 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300"></div>
          </button>

          {/* WhatsApp Button */}
          {whatsappNumber && (
            <div className="mt-4">
              <button
                onClick={handleWhatsAppUnitCode}
                className="group inline-flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                </svg>
                <span className="text-sm">لو الكود مش معك</span>
              </button>
            </div>
          )}

          {/* Additional Info */}
          <div className="mt-6 text-sm text-gray-500">
            <p>احصل على الكود من مستر وليد المصري لدخول المحاضرة</p>
          </div>
        </div>
      </section>

      {/* About Teacher Section */}
     

      {/* Floating WhatsApp Button with Speech Bubble */}
      {whatsappNumber && (
        <div className="fixed bottom-6 left-6 z-40 flex items-end gap-3">
          {/* Speech Bubble Message */}
          <div className="relative bg-white border border-gray-200 shadow-xl rounded-2xl p-4 max-w-64">
            <div className="text-gray-800 text-xs font-medium leading-relaxed">
             أهلا بيك يا عبقري .. تواصل معانا لو بتقابلك مشاكل
            </div>
            {/* Speech bubble tail */}
          </div>
          
          {/* WhatsApp Button */}
          <button
            onClick={handleWhatsAppSupport}
            className="group relative bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110"
          >
            {/* WhatsApp Icon */}
            <svg 
              className="w-6 h-6" 
              fill="currentColor" 
              viewBox="0 0 24 24"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
            </svg>

            {/* Pulse Animation */}
            <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20"></div>
          </button>
        </div>
      )}

      {/* Unit Code Popup */}
      <UnitCodePopup 
        isOpen={isUnitCodePopupOpen}
        onClose={() => setIsUnitCodePopupOpen(false)}
      />

    </div>
    </>
  );
};

export default React.memo(Home);
